import type { Chart } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/services/api';
import { isIntradayResolution, type Timeframe } from '@/types';
import type { WebMcpTool } from './webmcp';

// WebMCP tools: let an AI agent running in the browser (Chrome's agent, Claude in Chrome, MCP-B…)
// read the live chart — including exact indicator values and the user's drawings — and drive it.
// Registered from main.tsx before React renders, so agents (and agent-readiness scanners that check
// at the load event) find them on any page;
// chart tools report "no chart open" off the chart page. The chart helpers are imported lazily so
// this module stays out of the home page's critical bundle.

type Navigate = (path: string) => void;

let routerNavigate: Navigate | null = null;

/** Called by WebMcpBridge once the router is mounted, so tools navigate client-side. */
export function setWebMcpNavigate(fn: Navigate | null): void {
  routerNavigate = fn;
}

function navigate(path: string): void {
  if (routerNavigate) { routerNavigate(path); return; }
  // Router not mounted yet — BrowserRouter picks up history changes via popstate.
  history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

const TIMEFRAMES: Timeframe[] = ['1m', '5m', '15m', '30m', '1h', '1D', '1W', '1M'];
const AGENT_DRAWING_PREFIX = 'webmcp_';
const LOAD_TIMEOUT_MS = 10_000;

/** Overlay ids the agent created, per symbol — so it can list and clear only its own drawings. */
const agentDrawings: Array<{ id: string; symbol: string }> = [];

class ToolError extends Error {}

function ok(result: unknown) {
  return { content: [{ type: 'text', text: JSON.stringify(result) }] };
}

function fail(message: string) {
  return { content: [{ type: 'text', text: message }], isError: true };
}

function requireChart(): Chart {
  const { chart, loadedSymbol } = useChartStore.getState();
  if (!chart || !loadedSymbol || !location.pathname.startsWith('/chart/')) {
    throw new ToolError('No chart is open. Call open_chart with a symbol first.');
  }
  return chart;
}

function isIntraday(): boolean {
  return isIntradayResolution(useChartStore.getState().timeframe);
}

function dataSignature(chart: Chart | null): string {
  const data = chart?.getDataList() ?? [];
  return `${data.length}:${data[0]?.timestamp ?? ''}:${data[data.length - 1]?.timestamp ?? ''}`;
}

/** Resolve once `done()` holds, polling the store/chart; rejects after LOAD_TIMEOUT_MS. */
function waitFor(done: () => boolean, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (signal?.aborted) { reject(new ToolError('Cancelled.')); return; }
      if (done()) { resolve(); return; }
      if (Date.now() - started > LOAD_TIMEOUT_MS) { reject(new ToolError('Timed out waiting for the chart to load.')); return; }
      setTimeout(tick, 100);
    };
    tick();
  });
}

/** "2026-08-12", "2026-08-12 10:30" (Colombo time) or epoch ms → epoch ms. */
function parseTime(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?$/.exec(s);
    const ts = m ? Date.parse(`${m[1]}T${m[2] ?? '00:00'}:00+05:30`) : Date.parse(s);
    if (Number.isFinite(ts)) return ts;
  }
  throw new ToolError(`Invalid time "${String(v)}" — use "YYYY-MM-DD" or "YYYY-MM-DD HH:mm".`);
}

function num(v: unknown, field: string): number {
  const n = typeof v === 'string' ? Number(v) : v;
  if (typeof n !== 'number' || !Number.isFinite(n)) throw new ToolError(`"${field}" must be a number.`);
  return n;
}

function clampInt(v: unknown, def: number, min: number, max: number): number {
  const n = typeof v === 'number' ? Math.floor(v) : def;
  return Math.min(max, Math.max(min, Number.isFinite(n) ? n : def));
}

async function loadIndicatorDefs() {
  return (await import('@/components/Chart/indicatorDefs')).INDICATORS;
}

function buildTools(): WebMcpTool[] {
  return [
    // ── Site-wide ──────────────────────────────────────────────────────────
    {
      name: 'search_symbols',
      title: 'Search CSE symbols',
      description:
        'Search Colombo Stock Exchange stocks and indices by ticker or company name. '
        + 'Returns matching symbols (e.g. "JKH.N0000") to use with open_chart.',
      inputSchema: {
        type: 'object',
        properties: { query: { type: 'string', description: 'Ticker or company name, e.g. "john keells" or "JKH".' } },
        required: ['query'],
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const q = String(input.query ?? '').trim();
        if (!q) throw new ToolError('"query" is required.');
        const res = await api.search(q);
        return { count: res.count, results: res.results.slice(0, 20) };
      },
    },
    {
      name: 'open_chart',
      title: 'Open a chart',
      description:
        'Open (or switch to) the candlestick chart for a symbol, optionally at a timeframe, and wait for its data to load. '
        + 'Call this before any chart tool when no chart is open.',
      inputSchema: {
        type: 'object',
        properties: {
          symbol:    { type: 'string', description: 'CSE symbol, e.g. "JKH.N0000" or an index like "ASI". Use search_symbols if unsure.' },
          timeframe: { type: 'string', enum: TIMEFRAMES, description: 'Bar size. Defaults to the current one.' },
        },
        required: ['symbol'],
      },
      annotations: { readOnlyHint: false },
      async execute(input, options) {
        const sym = String(input.symbol ?? '').trim().toUpperCase();
        if (!sym) throw new ToolError('"symbol" is required.');
        const tf = input.timeframe as Timeframe | undefined;
        if (tf !== undefined && !TIMEFRAMES.includes(tf)) throw new ToolError(`"timeframe" must be one of ${TIMEFRAMES.join(', ')}.`);
        const store = useChartStore.getState();
        const onChart = location.pathname.startsWith('/chart/') && !!store.chart && store.chartInitialized;
        const needSymbol = !onChart || store.loadedSymbol !== sym;
        const needTf = tf !== undefined && tf !== store.timeframe;
        if (needSymbol || needTf) {
          const before = dataSignature(store.chart);
          if (needTf) store.setTimeframe(tf);
          if (needSymbol) navigate(`/chart/${encodeURIComponent(sym)}`);
          await waitFor(() => {
            const s = useChartStore.getState();
            // chartInitialized resets when the chart remounts, so a stale loadedSymbol from an
            // earlier visit can't satisfy this before the new chart has loaded.
            return !!s.chart && s.chartInitialized && s.loadedSymbol === sym
              && (needSymbol || dataSignature(s.chart) !== before);
          }, options?.signal);
        }
        const chart = requireChart();
        const bars = chart.getDataList().length;
        if (!bars) throw new ToolError(`No price data found for ${sym}. Check the symbol with search_symbols.`);
        return { symbol: sym, timeframe: useChartStore.getState().timeframe, bars };
      },
    },

    // ── Chart: read ────────────────────────────────────────────────────────
    {
      name: 'get_chart_state',
      title: 'Get chart state',
      description:
        'What the open chart is showing: symbol, timeframe, compared symbols, candle type, the active indicators with their '
        + 'parameters, and the number of user drawings.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      async execute() {
        const chart = requireChart();
        const [{ listIndicators, formatBarTime }, { buildCurrentCandleType }] = await Promise.all([
          import('@/utils/chartAccess'),
          import('@/components/Chart/templateSnapshot'),
        ]);
        const s = useChartStore.getState();
        const data = chart.getDataList();
        const rng = chart.getVisibleRange();
        const intraday = isIntraday();
        return {
          symbol: s.loadedSymbol,
          timeframe: s.timeframe,
          candleType: buildCurrentCandleType(chart) ?? 'candle_solid',
          compareSymbols: s.compareSymbols,
          bars: data.length,
          visibleRange: data.length ? {
            from: formatBarTime(data[Math.max(0, rng.from)].timestamp, intraday),
            to:   formatBarTime(data[Math.min(data.length - 1, rng.to)].timestamp, intraday),
          } : null,
          indicators: listIndicators(chart).map(({ name, paneId, params, series }) => ({
            name, pane: paneId, params, lines: series.map((x) => x.label),
          })),
          userDrawings: s.drawingOverlays.filter((d) => d.symbol === s.loadedSymbol && chart.getOverlayById(d.id)).length,
        };
      },
    },
    {
      name: 'get_visible_candles',
      title: 'Get candles',
      description:
        'OHLCV bars from the open chart, oldest first, as [date, open, high, low, close, volume]. '
        + 'Defaults to the bars currently in view.',
      inputSchema: {
        type: 'object',
        properties: {
          range:   { type: 'string', enum: ['visible', 'all'], description: '"visible" (default) = bars on screen; "all" = everything loaded.' },
          maxBars: { type: 'integer', minimum: 1, maximum: 1000, description: 'Most recent N bars within the range. Default 200.' },
        },
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const chart = requireChart();
        const { formatBarTime, round } = await import('@/utils/chartAccess');
        const data = chart.getDataList();
        const maxBars = clampInt(input.maxBars, 200, 1, 1000);
        let from = 0;
        let to = data.length - 1;
        if (input.range !== 'all') {
          const rng = chart.getVisibleRange();
          from = Math.max(0, rng.from);
          to = Math.min(data.length - 1, rng.to);
        }
        from = Math.max(from, to - maxBars + 1);
        const intraday = isIntraday();
        return {
          symbol: useChartStore.getState().loadedSymbol,
          timeframe: useChartStore.getState().timeframe,
          columns: ['date', 'open', 'high', 'low', 'close', 'volume'],
          bars: data.slice(from, to + 1).map((b) => [formatBarTime(b.timestamp, intraday), round(b.open), round(b.high), round(b.low), round(b.close), b.volume ?? 0]),
        };
      },
    },
    {
      name: 'get_last_bar',
      title: 'Get latest bar',
      description: 'The most recent bar on the open chart (or the bar under the user\'s crosshair if they are hovering one).',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      async execute() {
        requireChart();
        const s = useChartStore.getState();
        return { symbol: s.loadedSymbol, timeframe: s.timeframe, hovered: !!s.crosshair, bar: s.crosshair ?? s.lastBar };
      },
    },
    {
      name: 'list_indicators',
      title: 'List indicators',
      description:
        'Indicators active on the open chart with their parameters, pane, and the names of the lines each one plots '
        + '(e.g. MA → "MA20", "MA50"). Use the names with get_indicator_values.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      async execute() {
        const chart = requireChart();
        const { listIndicators } = await import('@/utils/chartAccess');
        const defs = await loadIndicatorDefs();
        const active = listIndicators(chart).map((i) => ({
          name: i.name,
          longName: defs.find((d) => d.name === i.name)?.longName,
          pane: i.paneId,
          onPricePane: i.onPricePane,
          visible: i.visible,
          params: i.params,
          lines: i.series.map((x) => x.label),
        }));
        return { active, available: defs.filter((d) => !d.hiddenFromBar).map((d) => d.name) };
      },
    },
    {
      name: 'get_indicator_values',
      title: 'Get indicator values',
      description:
        'Exact computed values of the indicators on the open chart (the numbers the chart plots — not estimates), per bar, '
        + 'with each bar\'s date and close. Includes a summary per indicator: latest value of each line, close\'s % distance '
        + 'from each price-pane line (e.g. moving averages), each line\'s % slope over the last 10 bars, and the most recent '
        + 'crossovers (line vs line, and close vs price-pane lines) with dates. Use for questions like "analyze the MAs".',
      inputSchema: {
        type: 'object',
        properties: {
          names:   { type: 'array', items: { type: 'string' }, description: 'Indicator names, e.g. ["MA", "RSI"]. Default: all active.' },
          range:   { type: 'string', enum: ['visible', 'all'], description: '"visible" (default) = bars on screen; "all" = everything loaded.' },
          maxBars: { type: 'integer', minimum: 1, maximum: 1000, description: 'Most recent N bars within the range. Default 200.' },
          summaryOnly: { type: 'boolean', description: 'Omit per-bar rows and return just the summary. Default false.' },
        },
      },
      annotations: { readOnlyHint: true },
      async execute(input) {
        const chart = requireChart();
        const { getIndicatorValues } = await import('@/utils/chartAccess');
        const names = Array.isArray(input.names) ? input.names.map(String) : undefined;
        const values = getIndicatorValues(chart, {
          names,
          range: input.range === 'all' ? 'all' : 'visible',
          maxBars: clampInt(input.maxBars, 200, 1, 1000),
          intraday: isIntraday(),
        });
        if (!values.length) {
          throw new ToolError(names?.length
            ? `None of ${names.join(', ')} is on the chart. Use list_indicators, or add_indicator first.`
            : 'No indicators are on the chart. Use add_indicator first.');
        }
        const s = useChartStore.getState();
        return {
          symbol: s.loadedSymbol,
          timeframe: s.timeframe,
          indicators: input.summaryOnly === true ? values.map((v) => ({ ...v, rows: undefined })) : values,
        };
      },
    },
    {
      name: 'get_drawings',
      title: 'Get chart drawings',
      description:
        'Drawings on the open chart — trendlines, horizontal levels, rectangles, Fibonacci tools, text boxes, etc. — with '
        + 'each anchor point\'s date and price. Includes the user\'s own drawings and any the agent added (byAgent: true).',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: true },
      async execute() {
        const chart = requireChart();
        const { listDrawings } = await import('@/utils/chartAccess');
        const s = useChartStore.getState();
        const sym = s.loadedSymbol;
        const intraday = isIntraday();
        const userIds = s.drawingOverlays.filter((d) => d.symbol === sym).map((d) => d.id);
        const agentIds = agentDrawings.filter((d) => d.symbol === sym).map((d) => d.id);
        return {
          symbol: sym,
          drawings: [
            ...listDrawings(chart, userIds, intraday).map((d) => ({ ...d, byAgent: false })),
            ...listDrawings(chart, agentIds, intraday).map((d) => ({ ...d, byAgent: true })),
          ],
        };
      },
    },

    // ── Chart: actions ─────────────────────────────────────────────────────
    {
      name: 'set_timeframe',
      title: 'Change timeframe',
      description: 'Change the open chart\'s bar size and wait for the new data to load.',
      inputSchema: {
        type: 'object',
        properties: { timeframe: { type: 'string', enum: TIMEFRAMES } },
        required: ['timeframe'],
      },
      annotations: { readOnlyHint: false },
      async execute(input, options) {
        const chart = requireChart();
        const tf = input.timeframe as Timeframe;
        if (!TIMEFRAMES.includes(tf)) throw new ToolError(`"timeframe" must be one of ${TIMEFRAMES.join(', ')}.`);
        const store = useChartStore.getState();
        if (store.timeframe !== tf) {
          const before = dataSignature(chart);
          store.setTimeframe(tf);
          await waitFor(() => dataSignature(useChartStore.getState().chart) !== before, options?.signal);
        }
        return { symbol: store.loadedSymbol, timeframe: tf, bars: requireChart().getDataList().length };
      },
    },
    {
      name: 'add_indicator',
      title: 'Add indicator',
      description:
        'Add an indicator to the open chart (e.g. MA, EMA, RSI, MACD, BOLL), optionally with custom parameters '
        + '(for MA/EMA/SMA each parameter is a period → one line, e.g. [20, 50, 200]). If it is already on the chart, '
        + 'only the parameters are updated. Use list_indicators for the available names.',
      inputSchema: {
        type: 'object',
        properties: {
          name:   { type: 'string', description: 'Indicator name, e.g. "MA".' },
          params: { type: 'array', items: { type: 'number' }, description: 'Calculation parameters. Default: the app defaults.' },
        },
        required: ['name'],
      },
      annotations: { readOnlyHint: false },
      async execute(input) {
        const chart = requireChart();
        const name = String(input.name ?? '').trim().toUpperCase();
        const defs = await loadIndicatorDefs();
        const def = defs.find((d) => d.name === name || d.label.toUpperCase() === name);
        if (!def || def.hiddenFromBar) throw new ToolError(`Unknown indicator "${String(input.name)}". Use list_indicators for the available names.`);
        const { SPECIAL_TOGGLE_INDICATORS, addStandardIndicator } = await import('@/components/Chart/indicatorOps');
        if (SPECIAL_TOGGLE_INDICATORS.has(def.name)) throw new ToolError(`${def.longName} has its own settings — ask the user to add it from the indicator bar.`);
        const plan = useAuthStore.getState().user?.plan;
        if (def.category === 'advanced' && plan !== 'pro' && plan !== 'business') {
          throw new ToolError(`${def.longName} is a Pro indicator and this account is on the ${plan ?? 'free'} plan.`);
        }

        const store = useChartStore.getState();
        if (!store.indicatorPaneMap.has(def.name)) {
          const added = await addStandardIndicator(chart, def);
          if (!added) throw new ToolError(`Couldn't load the data ${def.longName} needs. Try again shortly.`);
        }
        const params = Array.isArray(input.params) ? input.params.map((p, i) => num(p, `params[${i}]`)) : [];
        if (params.length) {
          const paneId = useChartStore.getState().indicatorPaneMap.get(def.name);
          (chart as Chart & { overrideIndicator: (o: { name: string; calcParams: number[] }, paneId?: string) => void })
            .overrideIndicator({ name: def.name, calcParams: params }, paneId);
          useChartStore.getState().setIndicatorParam(def.name, params);
        }
        const { listIndicators } = await import('@/utils/chartAccess');
        const info = listIndicators(chart).find((i) => i.name === def.name);
        return { added: def.name, params: info?.params, lines: info?.series.map((x) => x.label) };
      },
    },
    {
      name: 'remove_indicator',
      title: 'Remove indicator',
      description: 'Remove an indicator from the open chart.',
      inputSchema: {
        type: 'object',
        properties: { name: { type: 'string', description: 'Indicator name, e.g. "RSI".' } },
        required: ['name'],
      },
      annotations: { readOnlyHint: false },
      async execute(input) {
        const chart = requireChart();
        const name = String(input.name ?? '').trim().toUpperCase();
        const { SPECIAL_TOGGLE_INDICATORS, removeStandardIndicator } = await import('@/components/Chart/indicatorOps');
        if (SPECIAL_TOGGLE_INDICATORS.has(name)) throw new ToolError(`Ask the user to remove ${name} from the indicator bar.`);
        if (!useChartStore.getState().indicatorPaneMap.has(name)) throw new ToolError(`${name} is not on the chart.`);
        removeStandardIndicator(chart, name);
        return { removed: name };
      },
    },
    {
      name: 'draw_price_level',
      title: 'Draw price level',
      description: 'Draw a labelled horizontal line at a price on the open chart (e.g. support, resistance, target).',
      inputSchema: {
        type: 'object',
        properties: {
          price: { type: 'number' },
          label: { type: 'string', description: 'Short label, e.g. "Support".' },
          kind:  { type: 'string', enum: ['support', 'resistance', 'target', 'breakout_zone'], description: 'Sets the colour. Default "target".' },
        },
        required: ['price', 'label'],
      },
      annotations: { readOnlyHint: false },
      async execute(input) {
        const chart = requireChart();
        const { drawAIMarkings } = await import('@/utils/chartAccess');
        const kind = (['support', 'resistance', 'target', 'breakout_zone'] as const).find((k) => k === input.kind) ?? 'target';
        const color = { support: '#26a69a', resistance: '#ef5350', target: '#2962ff', breakout_zone: '#ff9800' }[kind];
        const price = num(input.price, 'price');
        const ids = drawAIMarkings(chart, [{ type: kind, price, label: String(input.label ?? '').slice(0, 40), color }], AGENT_DRAWING_PREFIX);
        const symbol = useChartStore.getState().loadedSymbol ?? '';
        ids.forEach((id) => agentDrawings.push({ id, symbol }));
        return { drawn: ids[0] ?? null, price };
      },
    },
    {
      name: 'draw_trendline',
      title: 'Draw trendline',
      description: 'Draw a straight line between two (date, price) points on the open chart. Dates snap to the nearest bar.',
      inputSchema: {
        type: 'object',
        properties: {
          from: { type: 'object', properties: { date: { type: 'string' }, price: { type: 'number' } }, required: ['date', 'price'] },
          to:   { type: 'object', properties: { date: { type: 'string' }, price: { type: 'number' } }, required: ['date', 'price'] },
          extend: { type: 'boolean', description: 'Extend the line to the right edge (a ray). Default false.' },
        },
        required: ['from', 'to'],
      },
      annotations: { readOnlyHint: false },
      async execute(input) {
        const chart = requireChart();
        const { nearestBarIndex, formatBarTime } = await import('@/utils/chartAccess');
        const data = chart.getDataList();
        if (!data.length) throw new ToolError('The chart has no data.');
        const pt = (p: unknown, field: string) => {
          const o = (p ?? {}) as { date?: unknown; price?: unknown };
          const idx = nearestBarIndex(data, parseTime(o.date));
          return { dataIndex: idx, timestamp: data[idx].timestamp, value: num(o.price, `${field}.price`) };
        };
        const a = pt(input.from, 'from');
        const b = pt(input.to, 'to');
        const id = chart.createOverlay({
          id: `${AGENT_DRAWING_PREFIX}${Date.now()}`,
          name: input.extend === true ? 'rayLine' : 'segment',
          points: [a, b],
          styles: { line: { color: '#2962ff', size: 1 } },
        });
        if (typeof id !== 'string') throw new ToolError('The chart rejected the drawing.');
        agentDrawings.push({ id, symbol: useChartStore.getState().loadedSymbol ?? '' });
        const intraday = isIntraday();
        return {
          drawn: id,
          from: { date: formatBarTime(a.timestamp, intraday), price: a.value },
          to:   { date: formatBarTime(b.timestamp, intraday), price: b.value },
        };
      },
    },
    {
      name: 'clear_agent_drawings',
      title: 'Clear agent drawings',
      description: 'Remove every drawing the agent added to the open chart. The user\'s own drawings are left untouched.',
      inputSchema: { type: 'object', properties: {} },
      annotations: { readOnlyHint: false },
      async execute() {
        const chart = requireChart();
        const sym = useChartStore.getState().loadedSymbol;
        let removed = 0;
        for (let i = agentDrawings.length - 1; i >= 0; i--) {
          if (agentDrawings[i].symbol !== sym) continue;
          if (chart.getOverlayById(agentDrawings[i].id)) { chart.removeOverlay({ id: agentDrawings[i].id }); removed++; }
          agentDrawings.splice(i, 1);
        }
        return { removed };
      },
    },
  ];
}

/** Wrap execute so expected failures reach the agent as a readable tool error, and results as MCP text content. */
function wrap(tool: WebMcpTool): WebMcpTool {
  return {
    ...tool,
    async execute(input, options) {
      try {
        return ok(await tool.execute(input ?? {}, options));
      } catch (e) {
        if (e instanceof ToolError) return fail(e.message);
        console.error(`[webmcp] ${tool.name} failed`, e);
        return fail(`${tool.name} failed: ${e instanceof Error ? e.message : String(e)}`);
      }
    },
  };
}

/**
 * Register the tools with the page's model context, if the browser has one. Returns a cleanup
 * function. Supports the current draft (registerTool + AbortSignal) and the earlier Chrome
 * preview (provideContext / unregisterTool).
 */
export function registerWebMcpTools(): () => void {
  // Chrome (153+) exposes the API as document.modelContext; earlier drafts and polyfills
  // (MCP-B, agent-readiness scanners) use navigator.modelContext.
  const mc = document.modelContext ?? navigator.modelContext;
  if (!mc) return () => {};
  const tools = buildTools().map(wrap);

  if (typeof mc.registerTool === 'function') {
    const controller = new AbortController();
    const handles: unknown[] = [];
    for (const tool of tools) {
      try {
        const handle = mc.registerTool(tool, { signal: controller.signal });
        handles.push(handle);
        // Newer builds return a promise — surface rejections (e.g. duplicate names) instead of leaking them.
        if (handle && typeof (handle as Promise<unknown>).catch === 'function') {
          (handle as Promise<unknown>).catch((e) => console.warn(`[webmcp] couldn't register ${tool.name}`, e));
        }
      } catch (e) {
        console.warn(`[webmcp] couldn't register ${tool.name}`, e);
      }
    }
    return () => {
      controller.abort();
      handles.forEach((h, i) => {
        const unregister = (h as { unregister?: () => void } | undefined)?.unregister;
        if (typeof unregister === 'function') { try { unregister(); } catch { /* ignore */ } }
        else if (typeof mc.unregisterTool === 'function') { try { mc.unregisterTool(tools[i].name); } catch { /* ignore */ } }
      });
    };
  }

  if (typeof mc.provideContext === 'function') {
    try { mc.provideContext({ tools }); } catch (e) { console.warn('[webmcp] provideContext failed', e); }
    return () => { try { mc.clearContext?.(); } catch { /* ignore */ } };
  }

  return () => {};
}
